import React, { useState, useEffect } from "react";
import axios from "../../api/axios";
import { getImageUrl } from "../../utility/imageHelper"; // ✅ Import image helper
import "./styles/AdminCategories.css"; // ✅ Import styles

import { useStore } from "../../context/StoreContext"; // ✅ Import StoreContext
import { regenerateSitemap } from "../../utility/setSitemap";


const AdminCategories = () => {
  const { fetchProducts } = useStore(); // ✅ Get fetchProducts function

  const [categories, setCategories] = useState([]);
  const [newCategory, setNewCategory] = useState("");
  const [categoryImage, setCategoryImage] = useState(null);
  const [csrfToken, setCsrfToken] = useState("");

  const [editCategory, setEditCategory] = useState({ id: null, name: "" });
  const [editCategoryImage, setEditCategoryImage] = useState(null);
  const [editCategoryImagePreview, setEditCategoryImagePreview] = useState(null); // ✅ New image preview
  const [editCategoryOldImage, setEditCategoryOldImage] = useState(null); // ✅ Store old image


  useEffect(() => {
    const token = document.cookie
      .split("; ")
      .find(row => row.startsWith("csrfToken="))
      ?.split("=")[1];

    if (token) setCsrfToken(token);
  }, []);

  // ✅ Fetch Categories
  const fetchCategories = async () => {
    try {
      const response = await axios.get("/api/products/categories", { withCredentials: true });
      setCategories(response.data.data);
    } catch (error) {
      console.error("🔥 Error fetching categories:", error.message);
    }
  };

  useEffect(() => {
    fetchCategories();
  }, []);

  const handleAddCategory = async (e) => {
    e.preventDefault();
    if (!csrfToken) return console.error("🚨 CSRF token is missing.");

    try {
      const categoryResponse = await axios.post("/api/admin/categories", { name: newCategory }, {
        withCredentials: true,
        headers: { "x-csrf-token": csrfToken },
      });

      const categoryId = categoryResponse.data.categoryId;
      setNewCategory("");

      if (categoryImage && categoryId) {
        const formData = new FormData();
        formData.append("image", categoryImage);

        await axios.post(`/api/admin/categories/${categoryId}/image`, formData, {
          withCredentials: true,
          headers: { "x-csrf-token": csrfToken, "Content-Type": "multipart/form-data" },
        });

        setCategoryImage(null);
      }

      fetchCategories();
      await regenerateSitemap();
    } catch (error) {
      console.error("🔥 Error adding category:", error.message);
    }
  };

  const handleUpdateCategory = async (e) => {
    e.preventDefault();
    if (!csrfToken) return console.error("🚨 CSRF token is missing.");

    const formData = new FormData();
    formData.append("name", editCategory.name);
    
    // ✅ Append image only if a new one is selected
    if (editCategoryImage) {
        formData.append("image", editCategoryImage);
    }

    try {
        await axios.put(`/api/admin/categories/${editCategory.id}`, formData, {
            withCredentials: true,
            headers: { "x-csrf-token": csrfToken },
        });

        setEditCategory({ id: null, name: "" });
        setEditCategoryImage(null);
        fetchCategories();
        await regenerateSitemap();
    } catch (error) {
        console.error("🔥 Error updating category:", error.message);
    }
};


  const handleDeleteCategory = async (categoryId) => {
    if (!window.confirm("Are you sure you want to delete this category?")) return;
    if (!csrfToken) return console.error("🚨 CSRF token is missing.");

    try {
      await axios.delete(`/api/admin/categories/${categoryId}`, {
        withCredentials: true,
        headers: { "x-csrf-token": csrfToken },
      });

      fetchCategories();
      fetchProducts();
      await regenerateSitemap();
    } catch (error) {
      console.error("🔥 Error deleting category:", error.message);
    }
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];

    if (file) {
        setEditCategoryImage(file); // ✅ Store new file
        setEditCategoryImagePreview(URL.createObjectURL(file)); // ✅ Create temporary preview
    }
};


  return (
    <div className="admin-categories-container">
      <h2>Manage Categories</h2>
      <p className="admin-note">Note: Deleting a category will remove all products within it.</p>

      {/* ✅ Add New Category */}
      <form className="category-form" onSubmit={handleAddCategory}>
        <label>What type of products will go in this category?</label>
        <input type="text" placeholder="New Category Name" value={newCategory} onChange={(e) => setNewCategory(e.target.value)} required />
        <label>Choose an image for the category.</label>
        <input type="file" accept="image/*" onChange={(e) => setCategoryImage(e.target.files[0])} />
        <button type="submit">Add Category</button>
      </form>

      <hr />

      {/* ✅ List of Categories */}
      <div className="categories-grid">
        {categories.length > 0 ? (
          categories.map((category) => (
            <div key={category.id} className="category-wrapper">
              {/* ✅ Category Card */}
              <div 
                className="category-card"
                style={{
                  backgroundImage: category.image_url ? `url(${getImageUrl(category.image_url)})` : "none",
                  backgroundSize: "cover",
                  backgroundPosition: "center",
                  backgroundRepeat: "no-repeat",
                }}
              >
                <span className="category-name">{category.name}</span>

                <div className="category-actions">
                  <button onClick={() => {
                      setEditCategory({ id: category.id, name: category.name });
                      setEditCategoryOldImage(category.image_url); // ✅ Store old image
                  }}>
                      ✏️ Edit
                  </button>

                  <button onClick={() => handleDeleteCategory(category.id)}>❌ Delete</button>
                </div>
              </div>

              

              {/* ✅ Ensure edit form appears directly below clicked category */}
              {editCategory.id === category.id && (
                <form className="category-form edit-form" onSubmit={handleUpdateCategory}>
                  <input
                    type="text"
                    placeholder="Edit Category Name"
                    value={editCategory.name}
                    onChange={(e) => setEditCategory({ ...editCategory, name: e.target.value })}
                    required
                  />
                  {/* ✅ Show old image & allow toggle */}
                  {editCategoryOldImage && (
                      <div className="image-preview-wrapper">
                          <p>Current Image:</p>
                          <img 
                              src={editCategoryImagePreview || getImageUrl(editCategoryOldImage)} 
                              alt="Category Preview" 
                              className="category-preview" 
                          />
                          {editCategoryImagePreview && (
                              <button 
                                  type="button" 
                                  className="restore-btn" 
                                  onClick={() => {
                                      setEditCategoryImage(null);
                                      setEditCategoryImagePreview(null);
                                  }}
                              >
                                  Restore Original
                              </button>
                          )}
                      </div>
                  )}

                  {/* ✅ Upload new image */}
                  <input type="file" accept="image/*" onChange={handleImageChange} />

                  <button type="submit">Update Category</button>
                  <button className="cancel-btn" onClick={() => setEditCategory({ id: null, name: "" })}>
                    Cancel
                  </button>
                </form>
              )}
            </div>
          ))
        ) : (
          <p>No categories found.</p>
        )}
      </div>
    </div>
  );
};

export default AdminCategories;
